import React, { useState, useEffect, useCallback } from 'react';
import Modal from './Modal';
import {
  getCustomerContracts,
  uploadCustomerContract,
  updateCustomerContract,
  deleteCustomerContract,
  getCustomerCompanies,
} from '../utils/api';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  DocumentTextIcon,
  ArrowTopRightOnSquareIcon,
  DocumentArrowUpIcon,
} from '@heroicons/react/24/outline';

const CustomerContractsModal = ({ isOpen, onClose, customer }) => {
  const [contracts, setContracts] = useState([]);
  const [linkedCompanies, setLinkedCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [saving, setSaving] = useState(false);

  // Get current user to check admin role
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = currentUser.role === 'admin';

  const [formData, setFormData] = useState({
    contract_name: '',
    contract_type: 'contract',
    company_id: '',
    effective_date: '',
    expiry_date: '',
    notes: '',
    file: null,
  });

  const loadContracts = useCallback(async () => {
    if (!customer?.id) return;
    setLoading(true);
    try {
      const [contractsRes, companiesRes] = await Promise.all([
        getCustomerContracts(customer.id),
        getCustomerCompanies(customer.id),
      ]);
      setContracts(contractsRes.data);
      setLinkedCompanies(companiesRes.data);
    } catch (error) {
      toast.error('Error loading contracts');
    } finally {
      setLoading(false);
    }
  }, [customer?.id]);

  useEffect(() => {
    if (isOpen && customer?.id) {
      loadContracts();
    }
  }, [isOpen, customer?.id, loadContracts]);

  const openFormModal = (contract = null) => {
    if (contract) {
      setEditingContract(contract);
      setFormData({
        contract_name: contract.contract_name || '',
        contract_type: contract.contract_type || 'contract',
        company_id: contract.company_id || '',
        effective_date: contract.effective_date ? contract.effective_date.split('T')[0] : '',
        expiry_date: contract.expiry_date ? contract.expiry_date.split('T')[0] : '',
        notes: contract.notes || '',
        file: null,
      });
    } else {
      setEditingContract(null);
      setFormData({
        contract_name: '',
        contract_type: 'contract',
        company_id: linkedCompanies.length === 1 ? linkedCompanies[0].id : '',
        effective_date: '',
        expiry_date: '',
        notes: '',
        file: null,
      });
    }
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    setFormModalOpen(false);
    setEditingContract(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.contract_name) {
      toast.error('Contract name is required');
      return;
    }
    if (linkedCompanies.length > 0 && !formData.company_id) {
      toast.error('Please select a company');
      return;
    }

    setSaving(true);
    try {
      if (editingContract) {
        await updateCustomerContract(customer.id, editingContract.id, {
          contract_name: formData.contract_name,
          contract_type: formData.contract_type,
          company_id: formData.company_id || null,
          effective_date: formData.effective_date || null,
          expiry_date: formData.expiry_date || null,
          notes: formData.notes || null,
        });
        toast.success('Contract updated successfully');
      } else {
        const data = new FormData();
        data.append('contract_name', formData.contract_name);
        data.append('contract_type', formData.contract_type);
        if (formData.company_id) data.append('company_id', formData.company_id);
        if (formData.effective_date) data.append('effective_date', formData.effective_date);
        if (formData.expiry_date) data.append('expiry_date', formData.expiry_date);
        if (formData.notes) data.append('notes', formData.notes);
        if (formData.file) data.append('file', formData.file);

        await uploadCustomerContract(customer.id, data);
        toast.success('Contract added successfully');
      }
      closeFormModal();
      loadContracts();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error saving contract');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (contract) => {
    if (!window.confirm(`Are you sure you want to delete "${contract.contract_name}"?`)) {
      return;
    }

    try {
      await deleteCustomerContract(customer.id, contract.id);
      toast.success('Contract deleted successfully');
      loadContracts();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error deleting contract');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={`Contracts - ${customer?.name || 'Customer'}`}
        size="lg"
        footer={
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        }
      >
        <div style={{ marginBottom: '1rem' }}>
          <button className="btn btn-primary" onClick={() => openFormModal()}>
            <PlusIcon style={{ width: 18, height: 18 }} />
            Add Contract
          </button>
        </div>

        {loading ? (
          <div className="loading-overlay" style={{ minHeight: 200 }}>
            <div className="spinner" />
            <span>Loading contracts...</span>
          </div>
        ) : contracts.length > 0 ? (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Our Company</th>
                  <th>Type</th>
                  <th>Effective Date</th>
                  <th>Expiry Date</th>
                  <th>File</th>
                  <th style={{ width: 120 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {contracts.map((contract) => (
                  <tr key={contract.id}>
                    <td>
                      <strong>{contract.contract_name}</strong>
                      {contract.notes && (
                        <div style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
                          {contract.notes.length > 50
                            ? contract.notes.substring(0, 50) + '...'
                            : contract.notes}
                        </div>
                      )}
                    </td>
                    <td>
                      {contract.company_name ? (
                        <span title={contract.company_name}>
                          {contract.company_code}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)' }}>-</span>
                      )}
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          contract.contract_type === 'amendment' ? 'badge-warning' : 'badge-info'
                        }`}
                      >
                        {contract.contract_type}
                      </span>
                    </td>
                    <td>{formatDate(contract.effective_date)}</td>
                    <td>{formatDate(contract.expiry_date)}</td>
                    <td>
                      {contract.file_url ? (
                        <a
                          href={contract.file_url.startsWith('/api')
                            ? `${process.env.REACT_APP_API_URL?.replace('/api', '')}${contract.file_url}`
                            : contract.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-ghost btn-sm"
                          title="View file"
                        >
                          <ArrowTopRightOnSquareIcon style={{ width: 16, height: 16 }} />
                          View
                        </a>
                      ) : contract.file_path ? (
                        <a
                          href={`${process.env.REACT_APP_API_URL}/customers/${customer.id}/contracts/file/${contract.file_path.split('/').pop()}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-ghost btn-sm"
                          title="View file"
                        >
                          <ArrowTopRightOnSquareIcon style={{ width: 16, height: 16 }} />
                          View
                        </a>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)' }}>-</span>
                      )}
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="btn btn-ghost btn-icon btn-sm"
                          onClick={() => openFormModal(contract)}
                          title="Edit"
                        >
                          <PencilIcon style={{ width: 16, height: 16 }} />
                        </button>
                        {isAdmin && (
                          <button
                            className="btn btn-ghost btn-icon btn-sm"
                            onClick={() => handleDelete(contract)}
                            title="Delete"
                            style={{ color: 'var(--color-accent-danger)' }}
                          >
                            <TrashIcon style={{ width: 16, height: 16 }} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state" style={{ padding: '2rem' }}>
            <DocumentTextIcon className="empty-state-icon" />
            <p className="empty-state-title">No contracts found</p>
            <p>Add a contract to get started</p>
          </div>
        )}
      </Modal>

      {/* Add/Edit Contract Modal */}
      <Modal
        isOpen={formModalOpen}
        onClose={closeFormModal}
        title={editingContract ? 'Edit Contract' : 'Add New Contract'}
        footer={
          <>
            <button className="btn btn-secondary" onClick={closeFormModal}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : editingContract ? 'Update Contract' : 'Add Contract'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          {/* View Contract File button when editing */}
          {editingContract && (editingContract.file_url || editingContract.file_path) && (
            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <a
                href={editingContract.file_url
                  ? (editingContract.file_url.startsWith('/api')
                    ? `${process.env.REACT_APP_API_URL?.replace('/api', '')}${editingContract.file_url}`
                    : editingContract.file_url)
                  : `${process.env.REACT_APP_API_URL}/customers/${customer.id}/contracts/file/${editingContract.file_path.split('/').pop()}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <ArrowTopRightOnSquareIcon style={{ width: 18, height: 18 }} />
                View Contract File
              </a>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Contract Name *</label>
            <input
              type="text"
              className="form-input"
              value={formData.contract_name}
              onChange={(e) => setFormData({ ...formData, contract_name: e.target.value })}
              placeholder="e.g., Service Agreement 2024"
            />
          </div>

          {linkedCompanies.length > 0 && (
            <div className="form-group">
              <label className="form-label">Our Company *</label>
              <select
                className="form-select"
                value={formData.company_id}
                onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
              >
                <option value="">Select our company...</option>
                {linkedCompanies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name} ({company.code})
                  </option>
                ))}
              </select>
              <small style={{ color: 'var(--color-text-muted)' }}>
                Which of our companies is signing this contract?
              </small>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Type</label>
              <select
                className="form-select"
                value={formData.contract_type}
                onChange={(e) => setFormData({ ...formData, contract_type: e.target.value })}
              >
                <option value="contract">Contract</option>
                <option value="amendment">Amendment</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Effective Date</label>
              <input
                type="date"
                className="form-input"
                value={formData.effective_date}
                onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Expiry Date</label>
            <input
              type="date"
              className="form-input"
              value={formData.expiry_date}
              onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea
              className="form-textarea"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes about this contract..."
              rows={3}
            />
          </div>

          {!editingContract && (
            <div className="form-group mb-0">
              <label className="form-label">
                <DocumentArrowUpIcon style={{ width: 18, height: 18, display: 'inline', marginRight: 8 }} />
                Upload File (PDF, Word, or Image)
              </label>
              <input
                type="file"
                className="form-input"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => setFormData({ ...formData, file: e.target.files[0] })}
              />
              <small style={{ color: 'var(--color-text-muted)' }}>Max size: 10MB</small>
            </div>
          )}
        </form>
      </Modal>
    </>
  );
};

export default CustomerContractsModal;
